import React, { useEffect, useMemo, useState } from "react";
import { getBlogs } from "../util/http";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import Nav from "./Nav";
import Footer from "./Footer";
import {
  BLOG_TOPIC_LABELS,
  buildBlogTagCounts,
  normalizeBlogTags,
  resolvePrimaryTopic,
} from "../lib/blogTopics";
import {
  ALLERGEN_SAFETY_PILLAR_PATH,
  ALLERGEN_SAFETY_QA_GUIDE_PATH,
  MENU_TRAINING_CHECKLIST_PATH,
  MENU_TRAINING_PILLAR_PATH,
  MENU_TRAINING_TEMPLATE_PATH,
  SERVER_TRAINING_CHECKLIST_PATH,
  SERVER_TRAINING_PILLAR_PATH,
  shouldShowContentClusterPromos,
  WINE_TRAINING_PAIRING_GUIDE_PATH,
  WINE_TRAINING_PILLAR_PATH,
} from "../lib/contentClusters";
import { SEO_SHARE_IMAGES } from "../lib/seoImages";
import "../styles/homepage-redesign.css";
import "../styles/faq-page.css";
import "../styles/BlogList.css";

const POSTS_PER_PAGE = 6;
const ALL_TOPIC = "All";
const MIN_TOPIC_POSTS = 3;
const START_FREE_HREF = "https://app.speakyourmenu.com/signup";
const BLOG_URL = "https://speakyourmenu.com/blog";
const BLOG_SHARE_IMAGE = SEO_SHARE_IMAGES.blog;

function toTimestamp(value) {
  const timestamp = new Date(value).getTime();
  return Number.isFinite(timestamp) ? timestamp : 0;
}

function formatDate(value) {
  const timestamp = toTimestamp(value);
  if (!timestamp) return "";

  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(timestamp);
}

function stripRichText(value) {
  return String(value || "")
    .replace(/```[\s\S]*?```/g, " ")
    .replace(/`([^`]+)`/g, "$1")
    .replace(/!\[[^\]]*\]\([^)]*\)/g, " ")
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
    .replace(/^#{1,6}\s+/gm, "")
    .replace(/[>*_~#-]/g, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function createExcerpt(post, maxLength = 170) {
  const source = stripRichText(post.excerpt || post.description || post.body || "");
  if (!source) return "No excerpt available yet.";
  if (source.length <= maxLength) return source;

  return `${source.slice(0, maxLength).trimEnd()}...`;
}

function createPullQuote(post) {
  const explicitQuote = stripRichText(post.pullQuote || post.quote || "");
  if (explicitQuote) return explicitQuote;

  const text = stripRichText(post.body || post.excerpt || post.description || "");
  if (!text) return "";

  const firstSentence = text.split(/(?<=[.!?])\s+/).find(Boolean) || text;
  const quote = firstSentence.length > 180 ? `${firstSentence.slice(0, 177).trimEnd()}...` : firstSentence;
  return quote.length >= 48 ? quote : "";
}

function estimateReadTime(post) {
  const wordCount = stripRichText(post.body || post.excerpt || post.description || "")
    .split(/\s+/)
    .filter(Boolean).length;

  const minutes = Math.max(1, Math.ceil(wordCount / 200));
  return `${minutes} min read`;
}

function isFeatured(post) {
  return post.featured === true || String(post.featured || "").toLowerCase() === "true";
}

function BlogList() {
  const showContentClusterPromos = shouldShowContentClusterPromos();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTopic, setActiveTopic] = useState(ALL_TOPIC);
  const [sortOrder, setSortOrder] = useState("newest");
  const [visibleCount, setVisibleCount] = useState(POSTS_PER_PAGE);

  useEffect(() => {
    async function fetch() {
      const all = await getBlogs();
      const now = new Date();
      const publishedPosts = (all || [])
        .filter((p) => {
          if (p.status !== "published") return false;
          if (!p.publishDate) return false;
          const pubDate = new Date(p.publishDate);
          return pubDate <= now;
        });

      setPosts(publishedPosts);
      setLoading(false);
    }
    fetch();
  }, []);

  const tagCounts = useMemo(() => buildBlogTagCounts(posts), [posts]);

  const preparedPosts = useMemo(
    () =>
      posts.map((post) => {
        const tags = normalizeBlogTags(post.tags);
        const excerpt = createExcerpt(post);
        const primaryTopic = resolvePrimaryTopic(post, tagCounts);

        return {
          ...post,
          tags,
          primaryTopic,
          excerpt,
          pullQuote: createPullQuote(post),
          readTime: estimateReadTime(post),
          publishTimestamp: toTimestamp(post.publishDate),
          formattedDate: formatDate(post.publishDate),
          searchBlob: [post.title, excerpt, post.description, post.body]
            .map((value) => stripRichText(value))
            .join(" ")
            .toLowerCase(),
        };
      }),
    [posts, tagCounts]
  );

  const topicCounts = useMemo(() => {
    const counts = BLOG_TOPIC_LABELS.reduce((result, topic) => {
      result[topic] = 0;
      return result;
    }, {});

    preparedPosts.forEach((post) => {
      counts[post.primaryTopic] = (counts[post.primaryTopic] || 0) + 1;
    });

    return counts;
  }, [preparedPosts]);

  const visibleTopics = useMemo(
    () => BLOG_TOPIC_LABELS.filter((topic) => (topicCounts[topic] || 0) >= MIN_TOPIC_POSTS),
    [topicCounts]
  );

  useEffect(() => {
    if (activeTopic !== ALL_TOPIC && !visibleTopics.includes(activeTopic)) {
      setActiveTopic(ALL_TOPIC);
    }
  }, [activeTopic, visibleTopics]);

  useEffect(() => {
    setVisibleCount(POSTS_PER_PAGE);
  }, [searchTerm, activeTopic, sortOrder]);

  const topicFilteredPosts = useMemo(() => {
    return preparedPosts.filter((post) => activeTopic === ALL_TOPIC || post.primaryTopic === activeTopic);
  }, [activeTopic, preparedPosts]);

  const filteredPosts = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();

    return topicFilteredPosts.filter((post) => !query || post.searchBlob.includes(query));
  }, [searchTerm, topicFilteredPosts]);

  const newestFirstPosts = useMemo(
    () => [...filteredPosts].sort((left, right) => right.publishTimestamp - left.publishTimestamp),
    [filteredPosts]
  );

  const featuredPost = useMemo(() => {
    if (!newestFirstPosts.length) return null;
    return newestFirstPosts.find(isFeatured) || newestFirstPosts[0];
  }, [newestFirstPosts]);

  const remainingPosts = useMemo(() => {
    const withoutFeatured = filteredPosts.filter((post) => post.id !== featuredPost?.id);

    return withoutFeatured.sort((left, right) => {
      if (sortOrder === "oldest") {
        return left.publishTimestamp - right.publishTimestamp;
      }

      return right.publishTimestamp - left.publishTimestamp;
    });
  }, [featuredPost, filteredPosts, sortOrder]);

  const visiblePosts = useMemo(
    () => remainingPosts.slice(0, visibleCount),
    [remainingPosts, visibleCount]
  );

  const totalVisible = (featuredPost ? 1 : 0) + visiblePosts.length;
  const totalFiltered = filteredPosts.length;
  const canLoadMore = visiblePosts.length < remainingPosts.length;
  const newestPosts = useMemo(
    () => [...preparedPosts].sort((left, right) => right.publishTimestamp - left.publishTimestamp),
    [preparedPosts]
  );
  const fallbackPosts = useMemo(() => newestPosts.slice(0, POSTS_PER_PAGE), [newestPosts]);
  const suggestedTopics = useMemo(
    () =>
      [...visibleTopics]
        .filter((topic) => topic !== activeTopic)
        .sort((left, right) => (topicCounts[right] || 0) - (topicCounts[left] || 0))
        .slice(0, 2),
    [activeTopic, topicCounts, visibleTopics]
  );

  return (
    <>
      <Helmet>
        <title>Blog | Speak Your Menu</title>
        <meta
          name="description"
          content="Practical menu training, onboarding, and sales tips for restaurant teams from the Speak Your Menu blog."
        />
        <meta property="og:title" content="Blog | Speak Your Menu" />
        <meta
          property="og:description"
          content="Practical menu training, onboarding, and sales tips for restaurant teams from the Speak Your Menu blog."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={BLOG_URL} />
        <meta property="og:image" content={BLOG_SHARE_IMAGE} />
        <meta property="og:site_name" content="Speak Your Menu" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Blog | Speak Your Menu" />
        <meta
          name="twitter:description"
          content="Practical menu training, onboarding, and sales tips for restaurant teams from the Speak Your Menu blog."
        />
        <meta name="twitter:image" content={BLOG_SHARE_IMAGE} />
        <link rel="canonical" href={BLOG_URL} />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Blog",
          "name": "Speak Your Menu Blog",
          "description": "Practical menu training, onboarding, and sales tips for restaurant teams.",
          "url": BLOG_URL,
          "image": BLOG_SHARE_IMAGE,
        })}</script>
      </Helmet>
      <Nav />
      <main id="main-content" tabIndex="-1" className="blog-index-page homepage-redesign faq-page">
        <section className="homepage-section homepage-hero-section faq-hero-section blog-hero-section">
          <div className="homepage-hero-bg" aria-hidden="true" />
          <div className="homepage-hero-glow homepage-hero-glow-left" aria-hidden="true" />
          <div className="homepage-hero-glow homepage-hero-glow-right" aria-hidden="true" />
          <div className="faq-hero-orb faq-hero-orb-left blog-hero-orb-left" aria-hidden="true" />
          <div className="faq-hero-orb faq-hero-orb-right blog-hero-orb-right" aria-hidden="true" />

          <div className="homepage-shell faq-hero-shell blog-hero-shell">
            <div className="faq-hero-copy blog-hero-copy">
              <span className="homepage-label-chip">Insights</span>
              <h1 className="homepage-hero-title faq-hero-title blog-hero-title">Blog</h1>
              <p className="homepage-hero-subhead faq-hero-subhead blog-hero-subhead">
                Practical menu training, onboarding, and sales tips for restaurant teams.
              </p>
            </div>

            <div className="faq-search-shell blog-search-shell">
              <div className="homepage-surface-card faq-search-card blog-search-card">
                <div className="faq-search-card-header blog-search-card-header">
                  <div>
                    <p className="faq-search-kicker">Browse articles</p>
                    <p className="faq-search-note blog-search-note">
                      Search by title or excerpt, then narrow by topic.
                    </p>
                  </div>
                  <p className="faq-search-summary">
                    {loading ? "Loading posts..." : `${totalFiltered} post${totalFiltered === 1 ? "" : "s"}`}
                  </p>
                </div>

                <div className="faq-search-form blog-search-form">
                  <div className="faq-search-input-wrap">
                    <input
                      type="search"
                      className="form-control faq-search-input blog-search-input"
                      placeholder="Search blog posts..."
                      value={searchTerm}
                      onChange={(event) => setSearchTerm(event.target.value)}
                      aria-label="Search blog posts"
                    />
                    {searchTerm ? (
                      <button
                        type="button"
                        className="faq-search-clear blog-search-clear"
                        onClick={() => setSearchTerm("")}
                      >
                        Clear
                      </button>
                    ) : null}
                  </div>
                </div>

                <div className="blog-controls-row">
                  <div className="faq-chip-bar blog-chip-bar" aria-label="Blog topics">
                    <button
                      type="button"
                      className={`faq-chip blog-chip${activeTopic === ALL_TOPIC ? " faq-chip-active" : ""}`}
                      onClick={() => setActiveTopic(ALL_TOPIC)}
                    >
                      <span>All</span>
                      <span className="blog-chip-count">{preparedPosts.length}</span>
                    </button>
                    {visibleTopics.map((topic) => (
                      <button
                        key={topic}
                        type="button"
                        className={`faq-chip blog-chip${activeTopic === topic ? " faq-chip-active" : ""}`}
                        onClick={() => setActiveTopic(topic)}
                      >
                        <span>{topic}</span>
                        <span className="blog-chip-count">{topicCounts[topic]}</span>
                      </button>
                    ))}
                  </div>

                  <label className="blog-sort-field">
                    <span className="blog-sort-label">Sort</span>
                    <select
                      className="form-select blog-sort-select"
                      value={sortOrder}
                      onChange={(event) => setSortOrder(event.target.value)}
                      aria-label="Sort blog posts"
                    >
                      <option value="newest">Newest</option>
                      <option value="oldest">Oldest</option>
                    </select>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="homepage-section blog-content-section">
          <div className="homepage-shell blog-content-shell">
            {showContentClusterPromos ? (
              <section className="homepage-surface-card resource-card blog-resource-card" aria-label="Training resources">
                <div className="resource-grid-two blog-resource-grid resource-grid-two-stackable">
                  <div>
                    <p className="blog-section-kicker">Menu training resources</p>
                    <h2 className="blog-section-title">Build context beyond individual posts.</h2>
                    <p className="blog-section-copy">
                      Start with the full menu training guide, then use the checklist to turn ideas from the blog into an operating rhythm for your team.
                    </p>
                  </div>
                  <div className="resource-inline-actions blog-resource-actions">
                    <Link to={MENU_TRAINING_PILLAR_PATH} className="homepage-primary-cta">Read the guide</Link>
                    <Link to={MENU_TRAINING_CHECKLIST_PATH} className="resource-secondary-link">Open checklist</Link>
                  </div>
                  <div>
                    <p className="blog-section-kicker">Server training resources</p>
                    <h2 className="blog-section-title">Tighten onboarding and first-week confidence.</h2>
                    <p className="blog-section-copy">
                      If the main issue is new-hire ramp speed or inconsistent early-shift execution, start with the server-training guide and onboarding checklist.
                    </p>
                  </div>
                  <div className="resource-inline-actions blog-resource-actions">
                    <Link to={SERVER_TRAINING_PILLAR_PATH} className="homepage-primary-cta">Read the guide</Link>
                    <Link to={SERVER_TRAINING_CHECKLIST_PATH} className="resource-secondary-link">Open onboarding checklist</Link>
                  </div>
                  <div>
                    <p className="blog-section-kicker">Wine training resources</p>
                    <h2 className="blog-section-title">Improve wine recommendations and beverage confidence.</h2>
                    <p className="blog-section-copy">
                      If the issue is weak wine language, uneven pairing confidence, or missed beverage upsell opportunities, start with the wine-training guide and pairing guide.
                    </p>
                  </div>
                  <div className="resource-inline-actions blog-resource-actions">
                    <Link to={WINE_TRAINING_PILLAR_PATH} className="homepage-primary-cta">Read the guide</Link>
                    <Link to={WINE_TRAINING_PAIRING_GUIDE_PATH} className="resource-secondary-link">Open pairing guide</Link>
                  </div>
                  <div>
                    <p className="blog-section-kicker">Allergen safety resources</p>
                    <h2 className="blog-section-title">Improve ingredient answers and dietary question handling.</h2>
                    <p className="blog-section-copy">
                      If the risk is inconsistent allergen or substitution answers, start with the allergen-safety guide and Q&amp;A guide.
                    </p>
                  </div>
                  <div className="resource-inline-actions blog-resource-actions">
                    <Link to={ALLERGEN_SAFETY_PILLAR_PATH} className="homepage-primary-cta">Read the guide</Link>
                    <Link to={ALLERGEN_SAFETY_QA_GUIDE_PATH} className="resource-secondary-link">Open Q&amp;A guide</Link>
                  </div>
                </div>
              </section>
            ) : null}
            {loading ? (
              <div className="blog-empty-state homepage-surface-card">
                <p className="blog-empty-kicker">Loading</p>
                <h2 className="blog-empty-title">Fetching the latest posts.</h2>
                <p className="blog-empty-copy">The blog index is pulling published entries from the existing database now.</p>
              </div>
            ) : totalFiltered === 0 ? (
              <div className="blog-empty-stack">
                <div className="blog-empty-state homepage-surface-card">
                  <p className="blog-empty-kicker">No results</p>
                  <h2 className="blog-empty-title">No posts match that search yet.</h2>
                  <p className="blog-empty-copy">
                    Try another keyword or jump to one of the busiest topics below. Recent articles are still shown so you do not hit a dead end.
                  </p>

                  {suggestedTopics.length ? (
                    <div className="blog-suggestion-row" aria-label="Suggested topics">
                      {suggestedTopics.map((topic) => (
                        <button
                          key={topic}
                          type="button"
                          className="blog-suggestion-button"
                          onClick={() => {
                            setSearchTerm("");
                            setActiveTopic(topic);
                          }}
                        >
                          {topic} ({topicCounts[topic]})
                        </button>
                      ))}
                    </div>
                  ) : null}

                  <div className="blog-empty-actions">
                    <button type="button" className="homepage-primary-cta blog-reset-button" onClick={() => {
                      setSearchTerm("");
                      setActiveTopic(ALL_TOPIC);
                      setSortOrder("newest");
                    }}>
                      Reset filters
                    </button>
                    <Link to="/" className="blog-secondary-link">
                      Return home
                    </Link>
                  </div>
                </div>

                <section className="blog-list-block" aria-labelledby="blog-fallback-heading">
                  <div className="blog-section-heading blog-section-heading-list">
                    <div>
                      <p className="blog-section-kicker">Recent fallback</p>
                      <h2 id="blog-fallback-heading" className="blog-section-title">Newest posts</h2>
                    </div>
                    <p className="blog-showing-count">Showing {fallbackPosts.length} recent posts</p>
                  </div>

                  <div className="blog-post-grid">
                    {fallbackPosts.map((post) => (
                      <article key={post.id} className="homepage-surface-card blog-post-card">
                        <div className="blog-post-card-top">
                          <span className="blog-category-badge blog-category-badge-muted">{post.primaryTopic}</span>
                          <h3 className="blog-post-title">
                            <Link to={`/blog/${post.slug}`}>{post.title}</Link>
                          </h3>
                          <p className="blog-post-excerpt">{post.excerpt}</p>
                        </div>

                        <div className="blog-post-card-bottom">
                          <div className="blog-meta-row">
                            <span>{post.formattedDate}</span>
                            <span aria-hidden="true">•</span>
                            <span>{post.readTime}</span>
                          </div>

                          {post.tags.length ? (
                            <div className="blog-tag-row" aria-label={`${post.title} tags`}>
                              {post.tags.slice(0, 2).map((tag) => (
                                <span key={tag} className="blog-tag-chip">{tag}</span>
                              ))}
                              {post.tags.length > 2 ? (
                                <span className="blog-tag-chip blog-tag-chip-muted">+{post.tags.length - 2}</span>
                              ) : null}
                            </div>
                          ) : null}

                          <Link to={`/blog/${post.slug}`} className="blog-card-read-link">
                            Read
                          </Link>
                        </div>
                      </article>
                    ))}
                  </div>
                </section>
              </div>
            ) : (
              <>
                {featuredPost ? (
                  <section className="blog-featured-block" aria-labelledby="blog-featured-heading">
                    <div className="blog-section-heading">
                      <div>
                        <p className="blog-section-kicker">Featured post</p>
                        <h2 id="blog-featured-heading" className="blog-section-title">Start here</h2>
                      </div>
                      <p className="blog-section-copy">A quick entry point into the latest thinking on menu training and restaurant team performance.</p>
                    </div>

                    <article className="homepage-surface-card blog-featured-card">
                      <div className="blog-featured-grid">
                        <div className="blog-featured-main">
                          <span className="blog-category-badge">{featuredPost.primaryTopic}</span>
                          <h3 className="blog-featured-title">
                            <Link to={`/blog/${featuredPost.slug}`}>{featuredPost.title}</Link>
                          </h3>
                          <div className="blog-meta-row">
                            <span>{featuredPost.formattedDate}</span>
                            <span aria-hidden="true">•</span>
                            <span>{featuredPost.readTime}</span>
                          </div>
                          <p className="blog-featured-excerpt">{featuredPost.excerpt}</p>

                          {featuredPost.tags.length ? (
                            <div className="blog-tag-row" aria-label="Featured post tags">
                              {featuredPost.tags.slice(0, 2).map((tag) => (
                                <span key={tag} className="blog-tag-chip">{tag}</span>
                              ))}
                              {featuredPost.tags.length > 2 ? (
                                <span className="blog-tag-chip blog-tag-chip-muted">+{featuredPost.tags.length - 2}</span>
                              ) : null}
                            </div>
                          ) : null}

                          <Link to={`/blog/${featuredPost.slug}`} className="homepage-primary-cta blog-read-link">
                            Read
                          </Link>
                        </div>

                        <aside className="blog-featured-aside">
                          <p className="blog-featured-aside-kicker">Quick takeaway</p>
                          {featuredPost.pullQuote ? <blockquote className="blog-featured-quote">“{featuredPost.pullQuote}”</blockquote> : null}
                          <p className="blog-featured-aside-copy">
                            Text-first posts, organized for faster scanning and easier follow-up reading.
                          </p>
                        </aside>
                      </div>
                    </article>
                  </section>
                ) : null}

                <section className="blog-list-block" aria-labelledby="blog-list-heading">
                  <div className="blog-section-heading blog-section-heading-list">
                    <div>
                      <p className="blog-section-kicker">Latest posts</p>
                      <h2 id="blog-list-heading" className="blog-section-title">Browse the archive</h2>
                    </div>
                    <p className="blog-showing-count">Showing {totalVisible} of {totalFiltered}</p>
                  </div>

                  <div className="blog-post-grid">
                    {visiblePosts.map((post) => (
                      <article key={post.id} className="homepage-surface-card blog-post-card">
                        <div className="blog-post-card-top">
                          <span className="blog-category-badge blog-category-badge-muted">{post.primaryTopic}</span>
                          <h3 className="blog-post-title">
                            <Link to={`/blog/${post.slug}`}>{post.title}</Link>
                          </h3>
                          <p className="blog-post-excerpt">{post.excerpt}</p>
                        </div>

                        <div className="blog-post-card-bottom">
                          <div className="blog-meta-row">
                            <span>{post.formattedDate}</span>
                            <span aria-hidden="true">•</span>
                            <span>{post.readTime}</span>
                          </div>

                          {post.tags.length ? (
                            <div className="blog-tag-row" aria-label={`${post.title} tags`}>
                              {post.tags.slice(0, 2).map((tag) => (
                                <span key={tag} className="blog-tag-chip">{tag}</span>
                              ))}
                              {post.tags.length > 2 ? (
                                <span className="blog-tag-chip blog-tag-chip-muted">+{post.tags.length - 2}</span>
                              ) : null}
                            </div>
                          ) : null}

                          <Link to={`/blog/${post.slug}`} className="blog-card-read-link">
                            Read
                          </Link>
                        </div>
                      </article>
                    ))}
                  </div>

                  {canLoadMore ? (
                    <div className="blog-load-more-row">
                      <button
                        type="button"
                        className="homepage-primary-cta blog-load-more-button"
                        onClick={() => setVisibleCount((current) => current + POSTS_PER_PAGE)}
                      >
                        Load more
                      </button>
                    </div>
                  ) : null}
                </section>
              </>
            )}
          </div>
        </section>

        <section className="homepage-section blog-cta-section">
          <div className="homepage-shell">
            {showContentClusterPromos ? (
              <div className="homepage-final-cta-shell blog-final-cta-shell">
                <div>
                  <span className="homepage-label-chip">Free training template</span>
                  <h2 className="homepage-section-title blog-cta-title">Get the free menu training template</h2>
                  <p className="homepage-section-body blog-cta-copy">
                    Grab the practical template or jump straight into the product if you are ready to tighten onboarding and menu fluency.
                  </p>
                </div>
                <div className="blog-cta-actions">
                  <Link to={MENU_TRAINING_TEMPLATE_PATH} className="homepage-primary-cta blog-cta-primary">Get the template</Link>
                  <a
                    href={START_FREE_HREF}
                    className="blog-secondary-link blog-cta-secondary"
                    data-cta-name="start_free"
                    data-cta-location="blog_final_cta"
                  >
                    Start free
                  </a>
                </div>
              </div>
            ) : null}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}

export default BlogList;
